import { useTranslation } from 'react-i18next';
import { MaterialSymbol, Skeleton, Tooltip, formatAsCurrency, withStyles } from 'vopay';
import { BodyText, Card } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import useProgramTermFormats from '@/hooks/useProgramTermFormats';
import type { ProgramTermsVariant } from './programTermsVariant';
import styles from './ProgramTermsCard.module.scss';

const cn = withStyles(styles);

type ProgramTermsCardProps = {
  variant: ProgramTermsVariant;
  fetching: boolean;
  revShare: string | null;
  minimumProgramFee: string | null;
};

export default function ProgramTermsCard({ variant, fetching, revShare, minimumProgramFee }: ProgramTermsCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const { permissions } = usePermissions();
  const { formatRevShare } = useProgramTermFormats();

  const value = variant === 'revShare' ? revShare : minimumProgramFee;
  const amount = value === null ? 0 : Number(value);

  // Missing, null or zero — including a VAR partner with no billing-settings row — hides the card;
  // it is never rendered empty or as a zero.
  if (!fetching && !(amount > 0)) return null;

  // The fee keeps two decimals as money.
  const formattedValue = variant === 'revShare' ? formatRevShare(amount) : formatAsCurrency(amount.toFixed(2));

  const tooltipCopy =
    variant === 'revShare'
      ? t('revShareTooltip', {
          defaultValue:
            "Your revenue share rate with VoPay. Each month you earn this percentage of your whole book's net margin — total revenue across all your accounts, minus VoPay's wholesale costs and other monthly costs. It is one rate for the entire book; it does not vary per account or per partner.",
        })
      : permissions.PartnerBillsAccount
        ? t('minimumProgramFeeBillsAccountsTooltip', {
            defaultValue:
              'The minimum monthly charge for your VoPay program. If your transaction fees for the month fall below this amount, the difference is added to your invoice as a Minimum Program Fee. The minimum is prorated for partial months, and only transaction fees count toward it — monthly platform and per-account fees are billed on top.',
          })
        : t('minimumProgramFeeRevShareTooltip', {
            defaultValue:
              "The minimum monthly wholesale commitment for your VoPay program. If your accounts' billed transaction fees for the month fall below this amount, the shortfall is applied as a Monthly Minimum Adjustment and deducted from your revenue share for that period.",
          });

  const label =
    variant === 'revShare'
      ? t('revShare', { defaultValue: 'Rev-Share' })
      : t('minimumProgramFee', { defaultValue: 'Min. Program Fee' });

  return (
    <Card variant="section" className={cn('card')} data-testid="programTermsCard">
      {fetching ? <Skeleton width={88} height={46} /> : <span className={cn('total')}>{formattedValue}</span>}

      <div className={cn('labelRow')}>
        {/* Body/16/Regular, Text/Secondary75 */}
        <BodyText className={cn('secondaryText')}>{label}</BodyText>

        {/* Figma `info` (13:94) is the plain Material Symbol, not Tooltip's default circled button. */}
        <Tooltip
          type="hover"
          position="bottom"
          width="340px"
          hasButton={false}
          customElement={<MaterialSymbol icon="info" size="16px" color="rgba(45, 45, 45, 0.5)" />}
          information={tooltipCopy}
        />
      </div>
    </Card>
  );
}
